import {
  attempt,
  cloneDeep,
  forOwn,
  get,
  isArray,
  isBoolean,
  isEmpty,
  isEqual,
  isError,
  isNaN,
  isNil,
  isNumber,
  isObject,
  isPlainObject,
  pull,
  uniqWith,
} from "lodash-es";
import Case from "case";

export const getNow = () => new Date() / 1000;

export const domId = (value) =>
  value
    ?.toString()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

export const isNumeric = (n) => {
  return !isNaN(parseFloat(n)) && isFinite(n);
};

export const isBlank = (v) => (isEmpty(v) && !isNumber(v) && !isBoolean(v)) || isNaN(v) || isNil(v);

// This renames keys in an object in order:
// rename({ one: 'first', two: 'second' }, { one: 'uno' }, { two: 'dos' })
// => { uno: "first", dos: 'second' }
export function rename(obj, ...changes) {
  const result = Object.assign({}, obj);
  changes.forEach((changeset) => {
    Object.keys(changeset).forEach((oldKey) => {
      const newKey = changeset[oldKey];
      result[newKey] = result[oldKey];
      delete result[oldKey];
    });
  });
  return result;
}

export const camelize = (str) => Case.camel(str);

export function getTitle(field, schema) {
  return get(schema, `${field}.title`, camelize(field));
}

export function getHelp(field, uiSchema) {
  return get(uiSchema, `${field}[ui:help]`);
}

export function getId(field, idSchema) {
  return get(idSchema, `${field}.$id`);
}

export function getClassNames(field, uiSchema) {
  return get(uiSchema, `${field}.classNames`);
}

export function errorToInputId(error) {
  if (!error.property) return null;

  const props = error.property.split(".").filter((x) => x);
  props.unshift("root");
  return props.join("_");
}

export const deepMapKeys = (obj, fn) => {
  var x = {};

  forOwn(obj, function (v, k) {
    if (isPlainObject(v)) v = deepMapKeys(v, fn);
    x[fn(v, k)] = v;
  });

  return x;
};

export const camelizeKeys = (collection) => deepMapKeys(collection, (v, k) => Case.camel(k));
export const snakifyKeys = (collection) => deepMapKeys(collection, (v, k) => Case.snake(k));

export const deepCompact = (obj) => {
  return (function compact(current) {
    // Recursively check for empty objects, arrays, and values
    // and remove them from the object.
    forOwn(current, (value, key) => {
      if (isBlank(value) || (isObject(value) && isEmpty(compact(value)))) {
        delete current[key];
      }
    });

    // remove any leftover undefined values from the delete
    // operation on an array
    // eslint-disable-next-line lodash/prefer-immutable-method
    if (isArray(current)) pull(current, undefined);

    return current;
  })(cloneDeep(obj));
};

// Adapted from https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another/5306832#5306832
export const arrayMove = (arr, old_index, new_index) => {
  const newArr = cloneDeep(arr);

  if (new_index >= newArr.length) {
    var k = new_index - newArr.length + 1;
    while (k--) {
      newArr.push(undefined);
    }
  }

  newArr.splice(new_index, 0, newArr.splice(old_index, 1)[0]);
  return newArr;
};

export const extractNameFromDataURL = (dataString) => {
  const nameRegex = /name=(.*?);/;
  const found = dataString.match(nameRegex);
  return found && found.length > 1 ? decodeURIComponent(found[1]) : "Unknown Filename";
};

export const findMappingId = (mappings, id, property) => mappings.find((m) => m[property] == id)?.id;

export const isValidJSON = (str) => !isError(attempt(JSON.stringify, str));
export const safeJSONParse = (v) => {
  try {
    return JSON.parse(v);
  } catch (e) {
    return v;
  }
};

export const uniqByShape = (collection) => uniqWith(collection, isEqual);
